// src/pages/kanban/components/ColumnaKanban.tsx
import { useState } from 'react';
import { Badge, Button } from '@/components/ui';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { TarjetaKanban } from './TarjetaKanban';
import type { ColumnaKanban as ColumnaKanbanType, TarjetaKanban as TarjetaKanbanData } from '@/types/kanban';
import { cn } from '@/lib/utils';

interface Props {
  columna: ColumnaKanbanType;
  tableroId: string;
  tamanoTarjeta?: 'compacto' | 'normal' | 'expandido';
  onDrop: (e: React.DragEvent, columnaDestino: string) => void;
  onDragStart: (e: React.DragEvent, tarjeta: TarjetaKanbanData) => void;
  onColapsar: (columna: string, colapsada: boolean) => void;
}

export function ColumnaKanban({
  columna,
  tableroId,
  tamanoTarjeta = 'normal',
  onDrop,
  onDragStart,
  onColapsar,
}: Props) {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Solo actualizar si realmente salimos de la columna
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    onDrop(e, columna.valor);
  };

  const toggleColapsar = () => {
    onColapsar(columna.valor, !columna.es_colapsada);
  };

  // Columna colapsada
  if (columna.es_colapsada) {
    return (
      <div
        className={cn(
          'flex flex-col items-center p-2 rounded-lg bg-gray-50 border-2 border-dashed border-gray-200 min-h-[200px] w-12 cursor-pointer hover:bg-gray-100 transition-colors',
          isDragOver && 'border-blue-400 bg-blue-50'
        )}
        onClick={toggleColapsar}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 mb-2">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <div
          className="writing-mode-vertical text-sm font-medium text-gray-700 transform rotate-180"
          style={{ writingMode: 'vertical-rl' }}
        >
          {columna.nombre}
        </div>
        <Badge className="mt-2">{columna.total}</Badge>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col rounded-lg bg-gray-50 border min-w-[300px] max-w-[300px] transition-colors',
        isDragOver && 'border-blue-400 bg-blue-100/50',
        columna.excede_limite && 'border-red-300 bg-red-50'
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header de columna */}
      <div
        className={cn(
          'flex items-center justify-between p-3 border-b',
          columna.color ? 'border-l-4' : '',
        )}
        style={columna.color ? { borderLeftColor: columna.color } : undefined}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 flex-shrink-0"
            onClick={toggleColapsar}
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <h3 className="font-semibold text-gray-900 truncate">{columna.nombre}</h3>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {columna.excede_limite && (
            <AlertTriangle className="h-4 w-4 text-red-500" />
          )}
          <Badge
            variant={columna.excede_limite ? 'danger' : 'default'}
            className="tabular-nums"
          >
            {columna.total}
            {columna.limite_wip && ` / ${columna.limite_wip}`}
          </Badge>
        </div>
      </div>

      {/* Contenedor de tarjetas */}
      <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-250px)] min-h-[100px]">
        {columna.tarjetas.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            Sin tarjetas
          </div>
        ) : (
          columna.tarjetas.map((tarjeta) => (
            <TarjetaKanban
              key={`${tarjeta.tipo}-${tarjeta.id}`}
              tarjeta={tarjeta}
              tableroId={tableroId}
              tamano={tamanoTarjeta}
              onDragStart={onDragStart}
            />
          ))
        )}
      </div>
    </div>
  );
}
